'use client';

import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ShieldAlert, DollarSign, Activity, FileText, KeyRound, Receipt, KeySquare, Store, ScrollText, Mail, Landmark } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { PARTNER_ADMIN_ROUTES } from '@/config/router';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';

interface PartnerAdminLayoutProps {
    children: React.ReactNode;
}

export default function PartnerAdminLayout({ children }: PartnerAdminLayoutProps) {
    const location = useLocation();
    const { partner: partnerParam } = useParams<{ partner: string }>();
    const navigate = useNavigate();
    const { canView, partner, isLoading } = usePartnerAdmin(partnerParam);

    const getCurrentTab = () => {
        if (location.pathname.endsWith('/processing')) return 'processing';
        if (location.pathname.endsWith('/reports')) return 'reports';
        if (location.pathname.endsWith('/invoiced')) return 'invoiced';
        if (location.pathname.endsWith('/billing')) return 'billing';
        if (location.pathname.endsWith('/merchants')) return 'merchants';
        if (location.pathname.endsWith('/team')) return 'team';
        if (location.pathname.endsWith('/crm-keys')) return 'crm-keys';
        if (location.pathname.endsWith('/email')) return 'email';
        if (location.pathname.endsWith('/payouts')) return 'payouts';
        return 'revenue';
    };

    const handleTabChange = (value: string) => {
        if (!partner) return;
        const routes = PARTNER_ADMIN_ROUTES(partner);
        const search = location.search;
        switch (value) {
            case 'revenue':
                navigate(routes.REVENUE + search);
                break;
            case 'invoiced':
                navigate(routes.INVOICED + search);
                break;
            case 'reports':
                navigate(routes.REPORTS);
                break;
            case 'processing':
                navigate(routes.PROCESSING + search);
                break;
            case 'billing':
                navigate(routes.BILLING);
                break;
            case 'merchants':
                navigate(routes.MERCHANTS);
                break;
            case 'team':
                navigate(routes.TEAM);
                break;
            case 'crm-keys':
                navigate(routes.CRM_KEYS);
                break;
            case 'email':
                navigate(routes.EMAIL);
                break;
            case 'payouts':
                navigate(routes.PAYOUTS);
                break;
        }
    };

    if (isLoading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <div className="text-muted-foreground text-sm">Loading...</div>
            </div>
        );
    }

    if (!canView || !partner) {
        return (
            <div className="bg-background flex min-h-[60vh] items-center justify-center">
                <div className="text-center">
                    <ShieldAlert className="text-muted-foreground mx-auto h-12 w-12" />
                    <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
                    <p className="text-muted-foreground mt-2 max-w-md">
                        This section is restricted to partner admins for <strong>{partnerParam ?? '(unset)'}</strong>.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Partner Admin</h1>
                            <Badge variant="secondary" className="text-xs uppercase">{partner}</Badge>
                        </div>
                        <p className="text-muted-foreground text-sm sm:text-base">
                            Partner-scoped revenue and processing analytics
                        </p>
                    </div>
                </div>

                <Tabs value={getCurrentTab()} onValueChange={handleTabChange} className="w-full">
                    <TabsList className="mb-6 w-full sm:w-auto">
                        <TabsTrigger value="revenue" className="gap-2">
                            <DollarSign className="h-4 w-4" />
                            Expected Revenue
                        </TabsTrigger>
                        <TabsTrigger value="invoiced" className="gap-2">
                            <FileText className="h-4 w-4" />
                            CRM Invoiced
                        </TabsTrigger>
                        <TabsTrigger value="reports" className="gap-2">
                            <ScrollText className="h-4 w-4" />
                            Statements
                        </TabsTrigger>
                        <TabsTrigger value="processing" className="gap-2">
                            <Activity className="h-4 w-4" />
                            Processing
                        </TabsTrigger>
                        <TabsTrigger value="billing" className="gap-2">
                            <Receipt className="h-4 w-4" />
                            Buy Rate
                        </TabsTrigger>
                        <TabsTrigger value="merchants" className="gap-2">
                            <Store className="h-4 w-4" />
                            Merchants
                        </TabsTrigger>
                        <TabsTrigger value="team" className="gap-2">
                            <KeyRound className="h-4 w-4" />
                            IAM
                        </TabsTrigger>
                        <TabsTrigger value="crm-keys" className="gap-2">
                            <KeySquare className="h-4 w-4" />
                            CRM keys
                        </TabsTrigger>
                        <TabsTrigger value="email" className="gap-2">
                            <Mail className="h-4 w-4" />
                            Email
                        </TabsTrigger>
                        <TabsTrigger value="payouts" className="gap-2">
                            <Landmark className="h-4 w-4" />
                            Payouts
                        </TabsTrigger>
                    </TabsList>
                </Tabs>

                <div>{children}</div>
            </div>
        </div>
    );
}
